import { useEffect, useMemo, useState } from 'react';
import { signedUrlFromStored, signedUrlsFromStored, StorageOptions } from '../utils/fileUrls';

/**
 * Resolve one stored file reference to a signed URL.
 *
 * Document buckets are private, so a column value cannot go straight into an
 * href or a src — it has to be exchanged for a short-lived signed link first.
 *
 *   const { url, loading, error } = useSignedUrl(driver.cdl_image_url);
 *
 * `url` is null while loading AND when signing failed; `error` tells them
 * apart. There is deliberately no fallback to the old public URL.
 *
 * Pass `bucket` only for a bare path (a column that stores the path without
 * the bucket); full stored URLs carry their bucket.
 */
export function useSignedUrl(stored: string | null | undefined, opts: StorageOptions = {}) {
  const value = String(stored || '').trim();
  const key = value ? JSON.stringify([opts.bucket ?? null, value]) : null;

  // The result remembers which file it was signed for. Everything returned is
  // derived from that, so the render right after `stored` changes can never
  // show the previous file's URL (right title, wrong document) or its error
  // state; it reads as loading until the new file resolves.
  const [result, setResult] = useState<{ for: string; url: string | null; error: boolean } | null>(null);

  useEffect(() => {
    if (!key) return;
    let cancelled = false;
    const [keyBucket, keyValue] = JSON.parse(key) as [string | null, string];

    signedUrlFromStored(keyValue, { bucket: keyBucket ?? undefined })
      .then(signed => {
        if (!cancelled) setResult({ for: key, url: signed, error: signed === null });
      })
      .catch(() => {
        if (!cancelled) setResult({ for: key, url: null, error: true });
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  const current = key !== null && result?.for === key;
  return {
    url: current ? result!.url : null,
    loading: key !== null && !current,
    error: current ? result!.error : false,
  };
}

/**
 * Resolve many at once — one request per bucket rather than one per file.
 *
 *   const { urls, loading } = useSignedUrls(photos);
 *   const src = urls.get(photo.trim());
 *
 * Look results up by the ORIGINAL stored value (trimmed). Absent from the map
 * while `loading` means "not yet"; absent once loading is false means signing
 * failed. Consumers must render those two states differently.
 */
export function useSignedUrls(stored: Array<string | null | undefined>, opts: StorageOptions = {}) {
  const bucket = opts.bucket;
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<boolean>(false);
  // Which key the map in state was resolved for. Deriving `loading` from this
  // (rather than a one-shot initializer) keeps a modal that opens with an
  // empty array and fills on the next render from painting a failure frame.
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);

  // Callers build the array inline, so key the effect on its contents. JSON
  // rather than a delimiter: these columns can hold free text.
  const key = useMemo(
    () => JSON.stringify([bucket ?? null, (stored || []).filter(Boolean).map(v => String(v).trim())]),
    [stored, bucket]
  );

  useEffect(() => {
    let cancelled = false;
    const [keyBucket, values] = JSON.parse(key) as [string | null, string[]];

    if (values.length === 0) {
      setUrls(new Map());
      setResolvedKey(key);
      setError(false);
      return;
    }

    setError(false);
    signedUrlsFromStored(values, { bucket: keyBucket ?? undefined })
      .then(map => {
        if (cancelled) return;
        setUrls(map);
        setError(map.size === 0);
      })
      .catch(() => {
        if (cancelled) return;
        setUrls(new Map());
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setResolvedKey(key);
      });

    return () => {
      cancelled = true;
    };
  }, [key]);

  const loading = resolvedKey !== key;
  return { urls, loading, error };
}
